import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, FileSpreadsheet, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import BulkImportDialog, { EMPLOYEE_TEMPLATE } from "@/components/BulkImportDialog";
import ConfirmDelete from "@/components/ConfirmDelete";

export default function Employees() {
  const { branch, user } = useAuth();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ branches: [], departments: [] });
  const [q, setQ] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = () => api.get("/employees").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    if (user?.role === "recruiter") return;
    load();
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, [user?.role]);

  const canManage = ["super_admin", "hr_manager"].includes(user?.role);

  const filtered = useMemo(() => {
    return items.filter((e) => {
      if (branch !== "All" && e.branch !== branch) return false;
      if (q && !`${e.name} ${e.department} ${e.designation}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [items, q, branch]);

  const openEdit = (e) => {
    setForm({
      id: e.id, name: e.name || "", phone: e.phone || "",
      designation: e.designation || "", department: e.department || "",
      branch: e.branch || "", joining_date: e.joining_date?.slice(0, 10) || "",
      employment_status: e.employment_status || "Active",
      satisfaction_status: e.satisfaction_status || "Not Checked Yet",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      const { id, ...body } = form;
      await api.patch(`/employees/${id}`, body);
      toast.success("Employee updated");
      setEditOpen(false);
      setForm({});
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Employee deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const docPill = (s) => ({ Complete: "rag-green", Partial: "rag-amber", Pending: "rag-red" }[s] || "rag-amber");
  const satPill = (s) => ({ Satisfied: "rag-green", Neutral: "rag-amber", Unsatisfied: "rag-red", "Not Checked Yet": "rag-amber" }[s] || "rag-amber");

  if (user?.role === "recruiter") {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-12 text-center" data-testid="employees-access-denied">
        <h1 className="font-heading text-xl font-bold text-[var(--yc-navy)]">Access restricted</h1>
        <p className="text-slate-600 text-sm mt-2">Recruiters do not have access to the employee master database.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Master Database</div>
          <h1 className="font-heading text-3xl font-bold text-[var(--yc-navy)] tracking-tight mt-1">Employees</h1>
          <p className="text-slate-600 text-sm mt-1">Documentation, satisfaction and tenure — auto-tracked from uploads and check-ins.</p>
        </div>
        {canManage && (
          <Button onClick={() => setBulkOpen(true)} variant="outline" className="rounded-full" data-testid="bulk-employees-button">
            <FileSpreadsheet className="h-4 w-4 mr-1" /> Bulk import
          </Button>
        )}
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input placeholder="Search employee…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" data-testid="employee-search-input" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100 bg-slate-50">
                <th className="data-cell">Employee</th>
                <th className="data-cell">Designation · Dept</th>
                <th className="data-cell">Branch</th>
                <th className="data-cell">Joining</th>
                <th className="data-cell">Tenure</th>
                <th className="data-cell">Docs</th>
                <th className="data-cell">Satisfaction</th>
                <th className="data-cell">Last Check-in</th>
                <th className="data-cell">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="data-cell text-center text-slate-500 py-10">No employees found.</td></tr>
              )}
              {filtered.map((e) => (
                <tr key={e.id} className="border-b border-slate-100 data-row" data-testid={`employee-row-${e.id}`}>
                  <td className="data-cell">
                    <div className="font-medium text-[var(--yc-navy)]">{e.name}</div>
                    <div className="text-xs text-slate-500">{e.phone}</div>
                  </td>
                  <td className="data-cell text-slate-600">{e.designation} <span className="text-slate-400">·</span> {e.department}</td>
                  <td className="data-cell text-slate-600">{e.branch}</td>
                  <td className="data-cell text-slate-600">{e.joining_date?.slice(0, 10)}</td>
                  <td className="data-cell font-mono text-xs">{e.tenure_days ?? 0}d</td>
                  <td className="data-cell">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${docPill(e.documentation_status)}`}>
                      {e.documentation_status}
                    </span>
                    {e.missing_documents?.length > 0 && (
                      <div className="text-[10px] text-slate-500 mt-1">Missing: {e.missing_documents.length}</div>
                    )}
                  </td>
                  <td className="data-cell">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${satPill(e.satisfaction_status)}`}>
                      {e.satisfaction_status}
                    </span>
                  </td>
                  <td className="data-cell text-slate-600 text-xs">{e.last_check_in_date?.slice(0, 10) || "—"}</td>
                  <td className="data-cell">
                    <div className="flex items-center gap-2">
                      <Link to={`/employees/${e.id}`} className="text-[var(--yc-emerald)] hover:underline text-sm whitespace-nowrap" data-testid={`view-employee-${e.id}`}>
                        Open profile →
                      </Link>
                      {canManage && (
                        <>
                          <button type="button" onClick={() => openEdit(e)} className="p-1.5 rounded-md text-slate-400 hover:text-[var(--yc-navy)] hover:bg-slate-100 transition-colors" title="Edit" data-testid={`edit-employee-${e.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <ConfirmDelete onConfirm={() => remove(e.id)} label={`employee "${e.name}"`} testId={`delete-employee-${e.id}`} />
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit employee</DialogTitle>
            <DialogDescription>Update employee master details.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="label-eyebrow">Name</Label>
              <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="employee-name-input" />
            </div>
            <div>
              <Label className="label-eyebrow">Phone</Label>
              <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="label-eyebrow">Designation</Label>
              <Input value={form.designation || ""} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="label-eyebrow">Department</Label>
              <Select value={form.department || ""} onValueChange={(v) => setForm({ ...form, department: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Choose department" /></SelectTrigger>
                <SelectContent>{meta.departments?.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Branch</Label>
              <Select value={form.branch || ""} onValueChange={(v) => setForm({ ...form, branch: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Choose branch" /></SelectTrigger>
                <SelectContent>{meta.branches?.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Joining date</Label>
              <Input type="date" value={form.joining_date || ""} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="label-eyebrow">Employment status</Label>
              <Select value={form.employment_status || "Active"} onValueChange={(v) => setForm({ ...form, employment_status: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["Active", "On Notice", "Exited"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Satisfaction</Label>
              <Select value={form.satisfaction_status || "Not Checked Yet"} onValueChange={(v) => setForm({ ...form, satisfaction_status: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["Satisfied", "Neutral", "Unsatisfied", "Not Checked Yet"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="save-employee-button">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkImportDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        endpoint="/employees/bulk"
        entityLabel="employees"
        sampleHeaders={EMPLOYEE_TEMPLATE}
        onDone={load}
      />
    </div>
  );
}
